import { useComponentState } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { GetStaticProps } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import Content from "src/components/Content";
import Footer from "src/components/Footer";
import Header from "src/components/Header";
import Headers from "src/components/Headers";
import ModalLogin from "src/components/ModalLogin";
import ModalNewPassword from "src/components/ModalNewPassword";
import Page from "src/components/Page";
import Preloader from "src/components/Preloader";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getDefaultOg from "src/functions/getDefaultOg";
import translate from "src/functions/translate";
import { useFilters } from "src/functions/useFilters";
import useGetDataInstructors, {
  getDataInstructors,
} from "src/functions/useGetDataInstructors";
import useGetDataSeriesAndClasses, {
  getDataSeriesAndClasses,
} from "src/functions/useGetDataSeriesAndClasses";
import useLoggedInUser from "src/functions/useLoggedInUser";
import { VariantColor } from "src/types/VariantColor";
// import FestivalHeroe from "src/customs/FestivalHeroe";
import Cta from "src/components/Cta";
import HeroBanner from "src/components/HeroBanner";
import HomePageContent from "src/components/HomePageContent";
import InstructorSlider from "src/components/InstructorSlider";
import InstructorStepper from "src/components/InstructorStepper";
import Newsletter from "src/components/Newsletter";
import WhyToUse from "src/components/WhyToUse";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sPageHome } from "src/styles/sPageHome.module.scss";

export default function Homepage({
  staticLectures,
  staticPrograms,
  staticInstructors,
}) {
  const { locale: lang, push } = useRouter();

  const filtersState = useFilters();
  const seriesAndClassesResult = useGetDataSeriesAndClasses(
    filtersState.value,
    "",
    false,
    true
  );
  const dClassesList =
    seriesAndClassesResult.data?.lectures || staticLectures || [];
  const dSeriesList =
    seriesAndClassesResult.data?.programs || staticPrograms || [];

  const instructorsResult = useGetDataInstructors(lang, "", false, true);
  var dInstructorList =
    instructorsResult.data?.profiles || staticInstructors || [];

  const [alertMessage, setAlertMessage] = useState("");
  const [alertMessageStatus, setAlertMessageStatus] = useState<VariantColor>();
  const [alertMessageBtn, setAlertMessageBtn] = useState(false);
  const [alertNewPassword, setAlertNewPassword] = useState(false);
  let openModalLogin = useComponentState(false);
  let openModalRegister = useComponentState(false);
  let openModalNewPassword = useComponentState(false);
  var userResult = useLoggedInUser();

  useEffect(() => {
    let loginConfirm =
      window.location.href.indexOf("account=confirm") > -1 ? true : false;
    if (loginConfirm) {
      setAlertMessage("Email confirmation was successful. Now you can");
      setAlertMessageStatus("success");
      setAlertMessageBtn(true);
    }
    let loginConfirmFailed =
      window.location.href.indexOf("account=confirmFailed") > -1 ? true : false;
    if (loginConfirmFailed) {
      setAlertMessage(
        "Email confirmation was not successful. You may have clicked on the link before. Try to sign in."
      );
      setAlertMessageStatus("danger");
      setAlertMessageBtn(false);
    }
    let registerSent =
      window.location.href.indexOf("account=pending") > -1 ? true : false;
    if (registerSent) {
      setAlertMessage(
        "Registration was successful. To log in, you need to confirm the link we sent you by e-mail."
      );
      setAlertMessageStatus("success");
      setAlertMessageBtn(false);
    }
    if (window.location.href.indexOf("pwdCode") > -1) {
      openModalNewPassword.change(true);
    }
  }, [openModalNewPassword]);

  useEffect(() => {
    const timeId = setTimeout(() => {
      // After 10 seconds set the show value to false
      setAlertNewPassword(false);
    }, 10000);

    return () => {
      clearTimeout(timeId);
    };
  }, [alertNewPassword]);

  return (
    <div id="root">
      <Headers data={getDefaultOg(lang)} />
      <Page className={composeClassNames([sPageHome, "page-home"])}>
        <Header />
        {alertMessage && !userResult.result && (
          <Alert
            variant={alertMessageStatus}
            className={composeClassNames([sAlert, "alert-flash"])}
          >
            <Translate tKey={alertMessage} />
            {alertMessageBtn && (
              <Button
                onClick={function () {
                  return openModalLogin.change(true);
                }}
                type={`button`}
                className={composeClassNames([sButton, "ml-2"])}
                variant={`primary`}
                size={`sm`}
              >
                <Translate tKey={`Sign in`} />
              </Button>
            )}
          </Alert>
        )}
        {alertNewPassword && (
          <Alert variant={`success`} className={sAlert}>
            <Translate
              tKey={`The password has been changed and the you are logged in.`}
            />
          </Alert>
        )}
        {/* TODO: context provider for modal login */}
        <ModalLogin
          openModalLogin={openModalLogin}
          openModalRegister={openModalRegister}
          key={`ModalLogin`}
        />
        <ModalNewPassword
          openModalNewPassword={openModalNewPassword}
          key={`ModalNewPassword`}
          setAlertNewPassword={setAlertNewPassword}
        />
        {constants.Show.homepageHero && <HeroBanner />}
        <Content fluid={`xxl`} verticalSpace={`lg`}>
          {seriesAndClassesResult.loading &&
          (!seriesAndClassesResult.data ||
            seriesAndClassesResult.data.length === 0) ? (
            <Preloader key="video-preloader" />
          ) : (
            <div></div>
          )}

          <HomePageContent series={dSeriesList} classes={dClassesList} />
          {instructorsResult.loading &&
          (!instructorsResult.data || instructorsResult.data.length === 0) ? (
            <Preloader key="instructors-preloader" />
          ) : (
            <div></div>
          )}
          {instructorsResult.error && !instructorsResult.data ? (
            <Alert variant="danger">
              {String(instructorsResult.error.message)}
            </Alert>
          ) : (
            <div></div>
          )}
        </Content>
        <WhyToUse backgroundColor={constants.Theme.greyLightOpacity} />
        <Content fluid={`full`} verticalSpace={`lg`}>
          <InstructorSlider
            data={dInstructorList}
            title={<Translate tKey={`Meet instructors from our community`} />}
            subtitle={
              <Translate
                tKey={`We give every instructor the opportunity to shine.`}
              />
            }
          />
          <InstructorStepper />
        </Content>
        <Cta
          title={
            "We are a group of yogis, webers and enthusiasts who want to make a difference"
          }
          text={
            "We have been in the world of yoga for some time, as well as in the development of web and mobile applications. We know how to build an applications you can rely on, so we decided to help the yoga community. In this way, instructors can focus on what they enjoy the most: yoga. They don't have to deal with the technology and bureaucracy around. And users-yogis can easily access interesting content."
          }
          videoSrc="https://youtu.be/RiEK6paBrY0"
          mediaPosition="right"
          button="Read full story"
          buttonClick={() => push("/story")}
          secondButton="Join the crew"
          secondButtonClick={() =>
            (window.location.href = translate(
              "mailto:team@anandita.life?Subject=Join the Anandita crew&body=Hi,%0A%0AI would like to join the Anandita team.%0AI am interested in the following position: %0A%0ABest regards",
              lang
            ))
          }
          backgroundColor={constants.Theme.greyLightOpacity}
        />
        <Cta
          title={"Exercise for every space, time and mood"}
          text={
            "Yoga is where you are. Whether in the studio, at home or outdoors. Sometimes you don't have the opportunity to go to the studio, or you don't know what to practice. At this moment, we are at your fingertips. You can start a class with your favorite teacher on a computer, tablet or even phone. Rather than replacing the natural environment, we want to supplement it."
          }
          imageSrc="/images/cta/cta-1.jpg"
          mediaPosition="left"
          button="Start exploring"
          buttonClick={() => push("/classes?paid=true")}
        />
        <Newsletter backgroundColor={constants.Theme.secondary} />
        <Footer />
      </Page>
    </div>
  );
}

export const getStaticProps: GetStaticProps = async function () {
  const { lectures, programs } = await getDataSeriesAndClasses();
  const instructors = await getDataInstructors();

  return {
    props: {
      staticLectures: lectures,
      staticPrograms: programs,
      staticInstructors: instructors,
    },
    revalidate: constants.RefreshIntervals.default,
  };
};
